import {useMemo} from "react";
import {usePage} from "@inertiajs/react";
import {observer} from "mobx-react";
import {Drawer} from "@mui/material";
import {useCatalogFilterDrawer} from "../model/useCatalogFilterDrawer";
import rootStores from "@/Stores/RootStores";
import {declOfNum} from "@/shared/lib/number/declOfNum";
import {Svg} from "@/shared/ui/Svg";
import {CatalogSortDto, CatalogSortOptionsFilter} from "@/features/catalog-sort";
import {CatalogOptionsFilter, CatalogRangeFilter} from "@/features/catalog-filters";
import {Button} from "@/shared/ui/Button";
import {CatalogDrawerFilter} from "../model/types";
import {mapCatalogSortOptions} from "@/features/catalog-sort";
import {useCatalogFiltersDrawer} from "@/widgets/catalog-filters-drawer";
import styles from "./CatalogFilterDrawer.module.scss";

type PageProps = {
    filters: CatalogDrawerFilter[];
    dataSort: CatalogSortDto[];
    insertColors: Record<string, string>
};

export const CatalogFilterDrawer = observer(() => {
    const {filters = [], dataSort = [], insertColors = {}} = usePage<PageProps>().props;

    const opened = useCatalogFilterDrawer(s => s.opened);
    const filterKey = useCatalogFilterDrawer(s => s.filterKey);
    const closeCatalogFilter = useCatalogFilterDrawer(s => s.actions.closeModal);
    const closeCatalogFilters = useCatalogFiltersDrawer(s => s.actions.closeModal);

    const {catalogStore} = rootStores;

    const filter = useMemo(() => {
        if (!filterKey) {
            return null;
        }
        if (filterKey === "sorting") {
            const options = mapCatalogSortOptions({
                options: dataSort,
            });
            return {type: "sort" as const, title: "Сортировка", options};
        }
        return filters.find((filter) => filter.key === filterKey);
    }, [filters, dataSort, filterKey]);

    const showProducts = () => {
        closeCatalogFilter();
        closeCatalogFilters();
    }

    if (!filter) return null;

    const renderContent = () => {
        switch (filter.type) {
            case "sort":
                return (
                    <CatalogSortOptionsFilter
                        options={filter.options}
                        selectedValue={catalogStore.selectedSortingValue}
                        checkboxContainerClassName={styles.labelContainer}
                        textPosition={`left`}
                        onChange={(option) => {
                            catalogStore.applySorting(option);
                            closeCatalogFilter();
                        }}
                    />
                );
            case "range":
                return (
                    <CatalogRangeFilter
                        option={filter.options}
                        onChange={(values) => {
                            catalogStore.onBlur(values, filter.options);
                        }}
                    />
                );
            default:
                return (
                    <CatalogOptionsFilter
                        options={filter.options}
                        showAll
                        insertColors={insertColors}
                        className={styles.drawerOptionsList}
                        checkboxContainerClassName={styles.labelContainer}
                        textPosition={`left`}
                        onChange={(option) => {
                            catalogStore.onChangeFilter(
                                option,
                                filter.dependencies,
                            );
                        }}
                    />
                );
        }
    };

    return (
        <Drawer
            id={`drawer`}
            anchor={`right`}
            open={opened}
            onClose={closeCatalogFilter}
            className={styles.catalogFilterDrawer}
            classes={{
                paper: styles.drawerPaper
            }}
        >
            <div className={styles.container}>
                <div className={styles.header}>
                    <button type={`button`} className={styles.backButton} onClick={closeCatalogFilter}>
                        <Svg id={`arrowDown`} className={styles.arrowIcon} />
                    </button>
                    <p className={styles.filterName}>{filter.title}</p>
                </div>
                <div className={styles.body}>
                    {renderContent()}
                </div>
                <div className={styles.footer}>
                    <Button fullWidth size={`xl`} onClick={showProducts}>
                        {catalogStore.catalogCount > 0 ?
                            <>
                                Показать {catalogStore.catalogCount.toLocaleString()} {declOfNum(catalogStore.catalogCount, [' товар', ' товара', ' товаров'])}
                            </>
                            : 'Товары не найдены'
                        }
                    </Button>
                </div>
            </div>
        </Drawer>
    );
});
